import React from "react"
import Layout from "../components/Layout"

const siteUrl = "https://gregwoodwardphoto.com"
const lastUpdated = "28 August 2026"

export default function Privacy() {
  return (
    <Layout
      seo={{
        title: "Privacy Policy | Greg Woodward",
        description:
          "How Greg Woodward Photography collects, uses and protects your personal data, including data submitted through the project enquiry form.",
        pathname: "/privacy/",
        schema: {
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "Privacy Policy",
          url: `${siteUrl}/privacy/`,
        },
      }}
    >
      <main className="journal-article">
        <div className="page-intro">
          <div>
            <span className="service-label">Privacy policy</span>
            <h1>How your data is handled.</h1>
          </div>
          <p>Last updated {lastUpdated}.</p>
        </div>

        <div className="journal-body">
          <h2>Who we are</h2>
          <p>
            This site is operated by Greg Woodward ("we", "us"), a
            commercial photography and video business based in London,
            United Kingdom. For any question about this policy or your
            data, contact{" "}
            <a href="mailto:info@gregwoodwardphoto.com">
              info@gregwoodwardphoto.com
            </a>
            .
          </p>

          <h2>What we collect</h2>
          <p>
            If you submit the project enquiry form on the{" "}
            <a href="/contact/">contact page</a>, we collect:
          </p>
          <ul>
            <li>Your name, company, email address and phone number</li>
            <li>
              Details about the project you describe: service type, scope,
              location, timing, intended usage, budget range and any notes
              you add
            </li>
            <li>
              A small amount of technical data used only to filter spam
              submissions (a timestamp and a hidden anti-spam field)
            </li>
          </ul>
          <p>
            We don't collect any personal data from simply browsing the
            site. If you arrive via a marketing link containing campaign
            parameters (for example <code>utm_source</code>), those
            parameters may be held briefly in your browser's session
            storage to understand which channel referred you. This clears
            automatically when you close the browser tab and is never
            combined with your identity unless you separately submit the
            enquiry form.
          </p>

          <h2>How we use it</h2>
          <p>
            Enquiry data is used to calculate an estimated project quote,
            review it before anything is sent, and email you that quote if
            we can help with your project. We may also use your contact
            details to follow up about the enquiry itself. We don't use
            enquiry data for marketing unless you separately opt in
            elsewhere.
          </p>
          <p>
            Where we use an AI service to help draft the wording of a quote
            email, only your name, company, project overview and any notes
            you provided are shared with that service, purely to write the
            email's opening paragraph. Pricing is always calculated
            separately and is never generated or altered by AI.
          </p>

          <h2>Who we share it with</h2>
          <p>
            We use a small number of third-party services to run this
            process, each acting as a data processor on our behalf:
          </p>
          <ul>
            <li>
              <strong>Netlify</strong>, who host this website and its
              enquiry-handling functions
            </li>
            <li>
              <strong>Google</strong> (Google Sheets), where enquiry records
              are stored
            </li>
            <li>
              <strong>Telegram</strong>, used to notify us of a new enquiry
              so it can be reviewed before any quote is sent
            </li>
            <li>
              our <strong>email provider</strong>, used to send the quote to
              you once approved
            </li>
            <li>
              <strong>OpenAI</strong>, only if used to help draft the
              wording described above
            </li>
          </ul>
          <p>
            We don't sell your data, and we don't share it with anyone
            outside this list except where required by law.
          </p>

          <h2>How long we keep it</h2>
          <p>
            We keep enquiry records only for as long as reasonably
            necessary to respond to your enquiry and for our business
            records. You can ask us to delete your data at any time using
            the contact details above.
          </p>

          <h2>Your rights</h2>
          <p>Under UK GDPR, you have the right to:</p>
          <ul>
            <li>ask what personal data we hold about you</li>
            <li>ask us to correct inaccurate data</li>
            <li>ask us to delete your data</li>
            <li>object to or restrict how we use it</li>
            <li>ask for a copy of your data in a portable format</li>
          </ul>
          <p>
            To exercise any of these rights, email{" "}
            <a href="mailto:info@gregwoodwardphoto.com">
              info@gregwoodwardphoto.com
            </a>
            . If you're not satisfied with our response, you can complain to
            the UK Information Commissioner's Office (ico.org.uk).
          </p>

          <h2>Cookies and analytics</h2>
          <p>
            This site does not currently use cookies or analytics trackers.
            If that changes, this policy will be updated first.
          </p>

          <h2>Changes to this policy</h2>
          <p>
            We may update this policy as the site or our processes change.
            The date at the top shows when it was last revised.
          </p>
        </div>
      </main>
    </Layout>
  )
}
